import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dropdown,
  Button,
  Checkbox,
  Space,
  Typography,
  Badge,
  Empty,
  Divider,
  Tabs,
  Layout,
  Card,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  DownOutlined,
  UserOutlined,
  ShopOutlined,
  BankOutlined,
  FilterOutlined,
  ClearOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { useHeaderStateProvider3 } from '@/context/HeaderStateContext3';
import { toApiDate } from '@/utility/to-api-date';
import FullscreenButton from '../screenToggle';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { useDateRange } from '@/context/DateRangeContext';
import { ProfileDropDown } from '../layout/ProfileDropDown';
import CountrySwitcher from '../country/CountrySwitcher';
import NotificationBell from '../notifications/NotificationBell';
import GlobalSearch from '../GlobalSearch';

const { Header } = Layout;
const { Text } = Typography;

interface ArrayDataType {
  label: string;
  id: number | string;
  key: string;
  spends?: number;
  accountId?: number | string; // Updated to handle number or string
  profileId?: string; // For profiles to store the actual profileId
}

interface SelectedDataType {
  account: ArrayDataType[];
  seller: ArrayDataType[];
  vendor: ArrayDataType[];
  profile: ArrayDataType[];
}

export interface SnapsuitePayloadDataType {
  selling_partner_ids: string[];
  profile_ids: string[];
  startDate: string;
  endDate: string;
  compareStartDate: string;
  compareEndDate: string;
}

type StoredDataType = {
  selectedItems: SelectedDataType;
  includeSbSpend: boolean;
  appliedAt: string;
  summary: {
    totalSelectedItems: number;
    accountTypes: string[];
    breakdown: { type: string; count: number }[];
  };
};

export default function DashboardHeaderTabbed() {
  const { setSnapsuitepayloaddata, adAccounts } = useHeaderStateProvider3();
  const { dateRange, compareRange } = useDateRange();

  // Transform data to match the required structure
  const transformData = useCallback(
    (data: any, type: string, accountId?: number | string): ArrayDataType[] => {
      return (data || []).map((item: any, index: number) => {
        const transformedItem: ArrayDataType = {
          label:
            item.name ||
            item.label ||
            item.accountName ||
            item.sellingPartnerId ||
            item.profileId ||
            item ||
            `Item ${index + 1}`,
          id:
            item.id ||
            item.tokenId ||
            item.profileId ||
            `amzn1.${type}.${index + 1}`,
          key: `${type}_${index + 1}`,
          spends:
            item.spends ||
            (type !== 'profile'
              ? Math.floor(Math.random() * 200000)
              : undefined),
        };

        if (type !== 'adacc') {
          transformedItem.accountId = accountId;
        }
        if (type === 'profile') {
          transformedItem.label = item.accountName || `Profile ${index + 1}`; // Use accountName for label
          transformedItem.id = item.profileId; // Use profileId as unique id
          transformedItem.accountId = item.linkedAccountId; // Link to parent account id
          transformedItem.profileId = item.profileId; // Store profileId
        }

        return transformedItem;
      });
    },
    []
  );

  // Memoize transformed data to avoid recomputation
  const transformedAccounts = useMemo(
    () => transformData(adAccounts, 'adacc'),
    [adAccounts, transformData]
  );
  const transformedSellers = useMemo(
    () =>
      adAccounts.flatMap((account) =>
        transformData(
          account?.sellerVendorDetails?.sellerDetails?.sellerPartnerIds || [],
          'sellerAccounts',
          account.id
        )
      ),
    [adAccounts, transformData]
  );
  const transformedVendors = useMemo(
    () =>
      adAccounts.flatMap((account) =>
        transformData(
          account?.sellerVendorDetails?.vendorDetails?.vendorPartnerIds || [],
          'vendorAccounts',
          account.id
        )
      ),
    [adAccounts, transformData]
  );
  const transformedProfiles = useMemo(
    () =>
      adAccounts.flatMap((account) =>
        transformData(account?.profiles || [], 'profile', account.id)
      ),
    [adAccounts, transformData]
  );

  const tabData = useMemo(
    () => ({
      account: transformedAccounts,
      seller: transformedSellers,
      vendor: transformedVendors,
      profile: transformedProfiles,
    }),
    [
      transformedAccounts,
      transformedSellers,
      transformedVendors,
      transformedProfiles,
    ]
  );

  // Create ID-to-key maps for sellers, vendors, and profiles
  const sellerIdToKey = useMemo(() => {
    const map: { [key: string]: string } = {};
    transformedSellers.forEach((seller) => {
      map[seller.label] = seller.key;
    });
    return map;
  }, [transformedSellers]);

  const vendorIdToKey = useMemo(() => {
    const map: { [key: string]: string } = {};
    transformedVendors.forEach((vendor) => {
      map[vendor.label] = vendor.key;
    });
    return map;
  }, [transformedVendors]);

  const profileIdToKey = useMemo(() => {
    const map: { [key: string]: string } = {};
    transformedProfiles.forEach((profile) => {
      map[profile.profileId || profile.id] = profile.key;
    });
    return map;
  }, [transformedProfiles]);

  // Tab configuration
  const tabConfig = useMemo(
    () => ({
      account: {
        icon: BankOutlined,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        lightColor: '#fef3c7',
        title: 'Ad Accounts',
        plural: 'accounts',
        description: 'Manage advertising accounts',
      },
      profile: {
        icon: ProfileOutlined,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        lightColor: '#fef3c7',
        title: 'AMS',
        plural: 'profiles',
        description: 'Manage profile settings',
      },
      seller: {
        icon: UserOutlined,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        lightColor: '#fef3c7',
        title: 'Sellers',
        plural: 'sellers',
        description: 'Monitor seller metrics',
      },
      vendor: {
        icon: ShopOutlined,
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        lightColor: '#fef3c7',
        title: 'Vendors',
        plural: 'vendors',
        description: 'Vendor partnerships',
      },
    }),
    []
  );

  // Initialize selected items (select all by default)
  const getInitialSelected = useCallback((items: ArrayDataType[]) => {
    const initial: { [key: string]: boolean } = {};
    items.forEach((item) => {
      initial[item.key] = true; // Select all items initially
    });
    return initial;
  }, []);

  const [activeTab, setActiveTab] = useState('account');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [includeSbSpend, setIncludeSbSpend] = useState(true);
  const [storedData, setStoredData] = useState<StoredDataType | null>(null);
  const [selectedItems, setSelectedItems] = useState({
    account: getInitialSelected(transformedAccounts),
    seller: getInitialSelected(transformedSellers),
    vendor: getInitialSelected(transformedVendors),
    profile: getInitialSelected(transformedProfiles),
  });

  // Sync selectedItems with transformed data when it changes
  useEffect(() => {
    setSelectedItems({
      account: getInitialSelected(transformedAccounts),
      seller: getInitialSelected(transformedSellers),
      vendor: getInitialSelected(transformedVendors),
      profile: getInitialSelected(transformedProfiles),
    });
  }, [
    transformedAccounts,
    transformedSellers,
    transformedVendors,
    transformedProfiles,
    getInitialSelected,
  ]);

  // Update Snapsuite payload when date range changes
  useEffect(() => {
    setSnapsuitepayloaddata((prev) => ({
      ...prev,
      startDate: toApiDate(dateRange.from),
      endDate: toApiDate(dateRange.to),
      compareStartDate: toApiDate(compareRange.from),
      compareEndDate: toApiDate(compareRange.to),
    }));
  }, [
    dateRange.from,
    dateRange.to,
    compareRange.from,
    compareRange.to,
    setSnapsuitepayloaddata,
  ]);

  // Update seller, vendor, and profile selections when account selections change
  useEffect(() => {
    setSelectedItems((prev) => {
      const newSelectedItems = { ...prev };
      const allSellerKeys = { ...prev.seller };
      const allVendorKeys = { ...prev.vendor };
      const allProfileKeys = { ...prev.profile };

      const selectedAccountKeys = Object.keys(prev.account).filter(
        (key) => prev.account[key]
      );

      // Reset sellers, vendors, and profiles
      Object.keys(allSellerKeys).forEach((key) => {
        allSellerKeys[key] = false;
      });
      Object.keys(allVendorKeys).forEach((key) => {
        allVendorKeys[key] = false;
      });
      Object.keys(allProfileKeys).forEach((key) => {
        allProfileKeys[key] = false;
      });

      // Update based on selected accounts
      selectedAccountKeys.forEach((accountKey) => {
        const account = tabData.account.find((item) => item.key === accountKey);
        if (account) {
          const matchedAccount = adAccounts.find(
            (item) => item.id === Number(account.id) // Convert to number to match account.id
          );
          if (matchedAccount) {
            const { sellerDetails, vendorDetails } =
              matchedAccount.sellerVendorDetails || {};
            if (
              sellerDetails?.hasSellerAccess &&
              sellerDetails?.sellerPartnerIds
            ) {
              sellerDetails.sellerPartnerIds.forEach((sellerId: string) => {
                const sellerKey = sellerIdToKey[sellerId];
                if (sellerKey) allSellerKeys[sellerKey] = true;
              });
            }
            if (
              vendorDetails?.hasVendorAccess &&
              vendorDetails?.vendorPartnerIds
            ) {
              vendorDetails.vendorPartnerIds.forEach((vendorId: string) => {
                const vendorKey = vendorIdToKey[vendorId];
                if (vendorKey) allVendorKeys[vendorKey] = true;
              });
            }
            if (matchedAccount.profiles) {
              matchedAccount.profiles.forEach((profile: any) => {
                const profileKey = profileIdToKey[profile.profileId];
                if (profileKey) allProfileKeys[profileKey] = true;
              });
            }
          }
        }
      });

      return {
        ...newSelectedItems,
        seller: allSellerKeys,
        vendor: allVendorKeys,
        profile: allProfileKeys,
      };
    });
  }, [
    adAccounts,
    tabData.account,
    sellerIdToKey,
    vendorIdToKey,
    profileIdToKey,
  ]);

  // Calculate dropdown height
  const calculateDropdownHeight = useCallback(() => {
    const items = tabData[activeTab] || [];
    const itemHeight = 48;
    const controlsHeight = 56;
    const footerBaseHeight = 64;
    const successMessageHeight = storedData ? 40 : 0;
    const tabBarHeight = 40;
    const minHeight = 250;
    const maxHeight = window.innerHeight * 0.8;
    const buffer = 10;

    return Math.min(
      Math.max(
        controlsHeight +
          items.length * itemHeight +
          footerBaseHeight +
          successMessageHeight +
          tabBarHeight +
          buffer,
        minHeight
      ),
      maxHeight
    );
  }, [activeTab, tabData, storedData]);

  const [dropdownHeight, setDropdownHeight] = useState(
    calculateDropdownHeight()
  );

  useEffect(() => {
    setDropdownHeight(calculateDropdownHeight());
  }, [calculateDropdownHeight]);

  const getFilteredItems = useCallback(
    (tabKey: string) => {
      const selectedAccountKeys = Object.keys(selectedItems.account).filter(
        (key) => selectedItems.account[key]
      );
      const selectedAccountIds = selectedAccountKeys
        .map((key) =>
          Number(tabData.account.find((item) => item.key === key)?.id)
        ) // Convert to number
        .filter((id): id is number => !!id);

      if (tabKey === 'seller') {
        return tabData.seller.filter((seller) =>
          selectedAccountIds.includes(Number(seller.accountId))
        );
      }
      if (tabKey === 'vendor') {
        return tabData.vendor.filter((vendor) =>
          selectedAccountIds.includes(Number(vendor.accountId))
        );
      }
      if (tabKey === 'profile') {
        return tabData.profile.filter((profile) =>
          selectedAccountIds.includes(Number(profile.accountId))
        );
      }
      return tabData[tabKey];
    },
    [tabData, selectedItems.account]
  );

  const handleSelectAll = useCallback(
    (tabKey: string, checked: boolean) => {
      setSelectedItems((prev) => {
        const items = getFilteredItems(tabKey);
        const updated = items.reduce(
          (acc, item) => {
            acc[item.key] = checked;
            return acc;
          },
          {} as { [key: string]: boolean }
        );
        return { ...prev, [tabKey]: { ...prev[tabKey], ...updated } };
      });
    },
    [getFilteredItems]
  );

  const handleClearAll = useCallback(
    (tabKey: string) => {
      setSelectedItems((prev) => {
        const items = getFilteredItems(tabKey);
        const cleared = items.reduce(
          (acc, item) => {
            acc[item.key] = false;
            return acc;
          },
          {} as { [key: string]: boolean }
        );
        return {
          ...prev,
          [tabKey]: cleared,
          ...(tabKey === 'account' && {
            seller: transformedSellers.reduce(
              (acc, item) => {
                acc[item.key] = false;
                return acc;
              },
              {} as { [key: string]: boolean }
            ),
            vendor: transformedVendors.reduce(
              (acc, item) => {
                acc[item.key] = false;
                return acc;
              },
              {} as { [key: string]: boolean }
            ),
            profile: transformedProfiles.reduce(
              (acc, item) => {
                acc[item.key] = false;
                return acc;
              },
              {} as { [key: string]: boolean }
            ),
          }),
        };
      });
    },
    [
      getFilteredItems,
      transformedSellers,
      transformedVendors,
      transformedProfiles,
    ]
  );

  const handleChange = useCallback(
    (tabKey: string, key: string, checked: boolean) => {
      setSelectedItems((prev) => {
        if (tabKey === 'account') {
          const account = tabData.account.find((item) => item.key === key);
          if (!account) return prev;

          const matchedAccount = adAccounts.find(
            (item) => item.id === Number(account.id)
          );
          if (!matchedAccount) {
            return { ...prev, account: { ...prev.account, [key]: checked } };
          }

          const { sellerDetails, vendorDetails } =
            matchedAccount.sellerVendorDetails || {};
          const newSellerKeys = { ...prev.seller };
          const newVendorKeys = { ...prev.vendor };
          const newProfileKeys = { ...prev.profile };

          if (
            sellerDetails?.hasSellerAccess &&
            sellerDetails?.sellerPartnerIds
          ) {
            sellerDetails.sellerPartnerIds.forEach((sellerId: string) => {
              const sellerKey = sellerIdToKey[sellerId];
              if (sellerKey) newSellerKeys[sellerKey] = checked;
            });
          }
          if (
            vendorDetails?.hasVendorAccess &&
            vendorDetails?.vendorPartnerIds
          ) {
            vendorDetails.vendorPartnerIds.forEach((vendorId: string) => {
              const vendorKey = vendorIdToKey[vendorId];
              if (vendorKey) newVendorKeys[vendorKey] = checked;
            });
          }
          if (matchedAccount.profiles) {
            matchedAccount.profiles.forEach((profile: any) => {
              const profileKey = profileIdToKey[profile.profileId];
              if (profileKey) newProfileKeys[profileKey] = checked;
            });
          }

          return {
            ...prev,
            account: { ...prev.account, [key]: checked },
            seller: newSellerKeys,
            vendor: newVendorKeys,
            profile: newProfileKeys,
          };
        }

        return { ...prev, [tabKey]: { ...prev[tabKey], [key]: checked } };
      });
    },
    [adAccounts, tabData.account, sellerIdToKey, vendorIdToKey, profileIdToKey]
  );

  const handleApply = useCallback(() => {
    const selectedData: SelectedDataType = {
      account: [],
      seller: [],
      vendor: [],
      profile: [],
    };

    Object.keys(selectedItems).forEach((tabKey) => {
      const selectedItemsForTab = Object.keys(selectedItems[tabKey])
        .filter((key) => selectedItems[tabKey][key])
        .map((key) => tabData[tabKey].find((item) => item.key === key))
        .filter((item): item is ArrayDataType => !!item);
      selectedData[tabKey] = selectedItemsForTab;
    });

    const vendorIds = selectedData.vendor.map((item) => item.label);
    const sellerIds = selectedData.seller.map((item) => item.label);
    const sellingPartnerIds = [...vendorIds, ...sellerIds];
    const profileIds = selectedData.profile
      .map((item) => item.profileId!)
      .filter((id): id is string => !!id);

    setSnapsuitepayloaddata({
      selling_partner_ids: sellingPartnerIds,
      profile_ids: profileIds,
      startDate: toApiDate(dateRange.from),
      endDate: toApiDate(dateRange.to),
      compareStartDate: toApiDate(compareRange.from),
      compareEndDate: toApiDate(compareRange.to),
    });

    setStoredData({
      selectedItems: selectedData,
      includeSbSpend,
      appliedAt: new Date().toISOString(),
      summary: {
        totalSelectedItems: Object.values(selectedItems).reduce(
          (total, items) => total + Object.values(items).filter(Boolean).length,
          0
        ),
        accountTypes: Object.keys(selectedData).filter(
          (key) => selectedData[key].length > 0
        ),
        breakdown: Object.keys(selectedData).map((tabKey) => ({
          type: tabKey,
          count: selectedData[tabKey].length,
        })),
      },
    });

    setDropdownVisible(false);
  }, [
    selectedItems,
    tabData,
    dateRange,
    compareRange,
    setSnapsuitepayloaddata,
    includeSbSpend,
  ]);

  const renderItemContent = (item: ArrayDataType) => (
    <div className="w-full">
      <Text strong className="text-sm block">
        {item.label}
      </Text>
      {item.accountId && (
        <Text type="secondary" className="text-xs">
          Account:{' '}
          {tabData.account.find((acc) => acc.id === item.accountId)?.label}
        </Text>
      )}
    </div>
  );

  const renderTabContent = (tabKey: string) => {
    const items = getFilteredItems(tabKey);
    const selected = selectedItems[tabKey] || {};
    const config = tabConfig[tabKey];
    const allChecked =
      items.length > 0 && items.every((item) => selected[item.key]);
    const indeterminate =
      items.some((item) => selected[item.key]) && !allChecked;

    return (
      <div className="flex flex-col bg-white flex-1">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <Checkbox
              checked={allChecked}
              indeterminate={indeterminate}
              onChange={(e) => handleSelectAll(tabKey, e.target.checked)}
              className="font-medium"
            >
              Select All ({items.length})
            </Checkbox>
            <Tooltip title="Clear all selections">
              <Button
                size="small"
                type="text"
                icon={<ClearOutlined />}
                onClick={() => handleClearAll(tabKey)}
              >
                Clear
              </Button>
            </Tooltip>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <Text type="secondary">No {config.plural} found</Text>
                    <br />
                    <Text type="secondary" className="text-xs">
                      Try adjusting your search criteria or select accounts
                    </Text>
                  </div>
                }
              />
            </div>
          ) : (
            items.map((item) => (
              <Card
                key={item.key}
                size="small"
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selected[item.key]
                    ? 'shadow-md border-blue-200 bg-blue-50'
                    : 'hover:shadow-sm'
                }`}
                onClick={() =>
                  handleChange(tabKey, item.key, !selected[item.key])
                }
                styles={{ body: { padding: 12 } }}
              >
                <div className="flex items-start space-x-3">
                  <Checkbox
                    checked={selected[item.key] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleChange(tabKey, item.key, e.target.checked);
                    }}
                    style={{ marginTop: '2px', marginRight: '8px' }}
                  />
                  <div className="flex-1">{renderItemContent(item)}</div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  };

  const getSelectedCount = useCallback(
    (tabKey: string) =>
      Object.values(selectedItems[tabKey] || {}).filter(Boolean).length,
    [selectedItems]
  );

  const getTotalSelectedAcrossAllTabs = useCallback(
    () =>
      Object.keys(selectedItems).reduce(
        (total, tabKey) => total + getSelectedCount(tabKey),
        0
      ),
    [selectedItems, getSelectedCount]
  );

  const createTabItems = useMemo(
    () =>
      Object.keys(tabConfig).map((tabKey) => {
        const config = tabConfig[tabKey];
        const IconComponent = config.icon;
        const selectedCount = getSelectedCount(tabKey);
        const totalCount = tabData[tabKey].length;
        const isAllSelected = selectedCount === totalCount && selectedCount > 0;

        return {
          key: tabKey,
          label: (
            <div className="flex items-center space-x-2 px-2 py-1">
              <IconComponent style={{ color: '#1890ff', fontSize: '16px' }} />
              <span className="font-medium">{config.title}</span>
              {selectedCount > 0 && (
                <Badge
                  count={`${selectedCount}/${totalCount}`}
                  style={{ backgroundColor: '#1890ff' }}
                  size="small"
                />
              )}
              {isAllSelected && (
                <CheckOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
              )}
            </div>
          ),
          children: renderTabContent(tabKey),
        };
      }),
    [tabConfig, tabData, getSelectedCount]
  );

  const dropdownContent = (
    <div
      className="bg-white rounded-lg shadow-xl border flex flex-col"
      style={{
        width: '500px',
        height: `${dropdownHeight}px`,
        boxSizing: 'border-box',
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={createTabItems}
        size="middle"
        tabBarStyle={{
          background: 'white',
          margin: 0,
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      />
      <div className="p-4 bg-gray-50 border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Text strong>
              Total Selected: {getTotalSelectedAcrossAllTabs()}
            </Text>
            <Divider type="vertical" />
            <Checkbox
              checked={includeSbSpend}
              onChange={(e) => setIncludeSbSpend(e.target.checked)}
            >
              <Text>Include SB Spend</Text>
            </Checkbox>
          </div>
          <Space>
            <Button onClick={() => setDropdownVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleApply}
              disabled={getTotalSelectedAcrossAllTabs() === 0}
            >
              Apply Filters
            </Button>
          </Space>
        </div>
      </div>
      {storedData && (
        <div className="p-3 bg-green-50 border-t border-green-200">
          <Text className="text-xs text-green-700">
            ✅ Filters applied successfully!{' '}
            {storedData.summary.totalSelectedItems} items selected
            {includeSbSpend ? ' | SB Spend included' : ' | SB Spend excluded'}
          </Text>
        </div>
      )}
    </div>
  );

  const getAccountTypeDisplay = useCallback(() => {
    const accountTypeCounts = Object.keys(selectedItems)
      .map((tabKey) => ({
        type: tabKey,
        count: getSelectedCount(tabKey),
        config: tabConfig[tabKey],
      }))
      .filter((item) => item.count > 0);

    if (accountTypeCounts.length === 0) {
      return { text: 'Select Account Type', hasSelection: false };
    }

    accountTypeCounts.sort((a, b) => b.count - a.count);
    const primary = accountTypeCounts[0];
    const text =
      accountTypeCounts.length === 1
        ? `${primary.config.title} (${primary.count})`
        : accountTypeCounts
            .map((item) => `${item.config.title} ${item.count}`)
            .join(' | ');

    return {
      text,
      hasSelection: true,
      icon: primary?.config.icon,
      multipleTypes: accountTypeCounts.length > 1,
    };
  }, [selectedItems, tabConfig, getSelectedCount]);

  const displayInfo = getAccountTypeDisplay();

  const collapseTriggerStyle = {
    backgroundColor: '#232f3e',
    color: 'rgba(255, 255, 255, 0.65)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'color 0.3s',
  };

  const getPageTitle = () => {
    const path = window.location?.pathname?.split('/').pop() || 'dashboard';
    return path
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  return (
    <Header
      style={{
        ...collapseTriggerStyle,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: '1.2rem',
          fontWeight: 500,
          lineHeight: '1.5715',
          textTransform: 'uppercase',
          color: '#fff',
          letterSpacing: '1.5px',
        }}
      >
        {getPageTitle()}
      </h2>

      <div className="flex items-center gap-4">
        <FullscreenButton />

        {/* Global Search */}
        <GlobalSearch placeholder="Search pages and features..." />

        <Space size="large">
          {/* <FullscreenButton /> */}
          <div className="pt-2">
            <Dropdown
              popupRender={() => dropdownContent}
              trigger={['click']}
              placement="bottom"
              open={dropdownVisible}
              onOpenChange={setDropdownVisible}
            >
              <Button
                size="small"
                style={{
                  minWidth: displayInfo.multipleTypes ? '350px' : '200px',
                  height: '30px',
                }}
              >
                <div className="flex items-center space-x-3">
                  <FilterOutlined style={{ fontSize: '16px' }} />
                  <span className="text-xs">{displayInfo.text}</span>
                  <DownOutlined style={{ fontSize: '12px' }} />
                </div>
              </Button>
            </Dropdown>
          </div>
          <DateRangePicker />
        </Space>
        <CountrySwitcher />
        <NotificationBell />
        <ProfileDropDown />
      </div>
    </Header>
  );
}
